// utils/authReturn.js
//
// Getting someone back to what they were doing, from the sign-in pages.
//
// A page that needs an account (Create at publish or Save as draft, a card)
// sends people to /login with `state.redirectTo` - where to take them after.
// That address has to survive the whole trip: /login -> /signup -> /profile-setup
// for someone new, and the back arrow on any of them. It used to be dropped
// at the first step off /login, and every back arrow went to the landing
// page - so a signed-out host who tapped Save as draft, then Back, lost the
// invitation they had just filled in.

/** The return address the sign-in pages were given, or null. */
export const authReturnPath = (location) => location?.state?.redirectTo || null;

/**
 * How many sign-in pages deep this one is, counted from the page that sent
 * them (1 for /login opened from an invitation, 2 for /signup reached from
 * there), or 0 when unknown.
 */
export const authDepth = (location) => Number(location?.state?.authDepth) || 0;

/**
 * Router state for the next auth page: the return address, and - when the
 * trip started from a page we can step back to - one more step of depth.
 */
export const withReturn = (redirectTo, depth = 0) => {
  if (!redirectTo) return undefined;
  return depth > 0 ? { redirectTo, authDepth: depth } : { redirectTo };
};

/**
 * The back arrow on a sign-in page: to what they were doing if we know it,
 * otherwise back a step, otherwise home.
 *
 * When we know how many sign-in pages they came through, it steps back that
 * many - the very page they left, with its history entry intact, so the
 * browser's own Back from there goes where it did before. Replacing the entry
 * with a fresh copy of the page (the fallback) left history reading
 * invitation -> login -> invitation, and Back went to the login page again.
 */
export function leaveAuth(navigate, redirectTo, depth = 0) {
  const idx = window.history.state?.idx ?? 0;
  if (redirectTo && depth > 0 && idx >= depth) {
    navigate(-depth);
  } else if (redirectTo) {
    navigate(redirectTo, { replace: true });
  } else if (idx > 0) {
    navigate(-1);
  } else {
    navigate('/');
  }
}
